import { CircularProgress, Box, Typography, Fade, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useRef } from "react";
import moment from "moment";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

// Styled Components
const NotificationPopover = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'linear-gradient(135deg, #1f1f1f 0%, #2d2d2d 100%)',
});

const HeaderSection = styled(Box)({
  padding: '16px 20px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'rgba(26, 115, 232, 0.08)',
});

const HeaderTitle = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#ffffff',
  fontFamily: "'DM Sans', sans-serif",
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '& svg': {
    color: '#1a73e8',
  },
});

const NotificationCount = styled(Chip)({
  height: '24px',
  fontSize: '0.75rem',
  fontWeight: 600,
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  color: 'white',
  '& .MuiChip-label': {
    padding: '0 10px',
  },
});

const ContentContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'linear-gradient(180deg, #1a73e8 0%, #00bfa5 100%)',
    borderRadius: '10px',
    '&:hover': {
      background: 'linear-gradient(180deg, #1557b0 0%, #009688 100%)',
    },
  },
});

const NotificationItem = styled(Box)({
  padding: '18px 20px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  transition: 'all 0.3s ease',
  position: 'relative',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: '4px',
    background: 'linear-gradient(180deg, #1a73e8 0%, #00bfa5 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    background: 'rgba(26, 115, 232, 0.12)',
    '&::before': {
      opacity: 1,
    },
  },
  '&:last-child': {
    borderBottom: 'none',
  },
});

const NotificationHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '8px',
});

const NotificationDot = styled(FiberManualRecordIcon)({
  fontSize: '10px',
  color: '#00bfa5',
  marginTop: '6px',
  flexShrink: 0,
});

const NotificationText = styled(Typography)({
  fontSize: '0.9375rem',
  lineHeight: 1.6,
  color: '#e8eaed',
  fontFamily: "'DM Sans', sans-serif",
  flex: 1,
  fontWeight: 500,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  '& a': {
    color: '#1a73e8',
    textDecoration: 'none',
    fontWeight: 600,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: '#00bfa5',
      textDecoration: 'underline',
    },
  },
});

const DateTimeWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '8px',
  paddingLeft: '22px',
});

const DateTimeText = styled(Typography)({
  fontSize: '0.8125rem',
  color: '#9aa0a6',
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 500,
});

const NoNotification = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  padding: '60px 20px',
});

const LoaderContainer = styled(Box)({
  padding: '12px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'rgba(0, 0, 0, 0.2)',
});

function Notification(props) {
  const { notification = [], notificationLoader = false } = props;

  const notificationRef = useRef(null);

  const renderNotificationContent = (text) => {
    const textSplit = text.split(" ");
    return textSplit.map((word, index) => {
      if (word.includes("http")) {
        return (
          <a key={index} href={word} target="_blank" rel="noreferrer">
            {word}{" "}
          </a>
        );
      } else {
        return <span key={index}>{word} </span>;
      }
    });
  };

  return (
    <NotificationPopover className="notification-popover">
      {/* Header Section */}
      <HeaderSection>
        <HeaderTitle>
          <NotificationsActiveIcon sx={{ fontSize: 24 }} />
          Notifications
          {notification.length > 0 && (
            <NotificationCount label={notification.length} size="small" />
          )}
        </HeaderTitle>
      </HeaderSection>

      {/* Content Section */}
      <ContentContainer className="content-container" ref={notificationRef}>
        {notification.length ? (
          notification.map((item, index) => {
            const text = item.notification || "";
            const date = item.created_at || "";
            return (
              <Fade in key={index} timeout={300 + index * 50}>
                <NotificationItem>
                  <NotificationHeader>
                    <NotificationDot />
                    <NotificationText>
                      {renderNotificationContent(text)}
                    </NotificationText>
                  </NotificationHeader>
                  <DateTimeWrapper>
                    <AccessTimeIcon sx={{ fontSize: 14, color: '#9aa0a6' }} />
                    <DateTimeText>
                      {moment(date).format("hh:mm A • DD/MM/YYYY")}
                    </DateTimeText>
                  </DateTimeWrapper>
                </NotificationItem>
              </Fade>
            );
          })
        ) : (
          <Fade in timeout={500}>
            <NoNotification className="no-notification">
              <NotificationsNoneIcon
                sx={{
                  fontSize: 72,
                  color: '#5f6368',
                  opacity: 0.4,
                  filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))',
                }}
              />
              <Typography
                sx={{
                  color: '#9aa0a6',
                  fontSize: '1.125rem',
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                }}
              >
                No notifications yet
              </Typography>
              <Typography
                sx={{
                  color: '#6c757d',
                  fontSize: '0.9375rem',
                  fontFamily: "'DM Sans', sans-serif",
                  textAlign: 'center',
                  maxWidth: '280px',
                  lineHeight: 1.5,
                }}
              >
                You're all caught up! New notifications will appear here.
              </Typography>
            </NoNotification>
          </Fade>
        )}
      </ContentContainer>

      {/* Loader Section */}
      {notificationLoader ? (
        <LoaderContainer className="loader-container">
          <CircularProgress
            size={24}
            sx={{
              color: '#1a73e8',
            }}
          />
          <Typography
            sx={{
              ml: 2,
              color: '#9aa0a6',
              fontSize: '0.875rem',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Loading...
          </Typography>
        </LoaderContainer>
      ) : null}
    </NotificationPopover>
  );
}

export default Notification;
